import { useLayoutEffect, useRef } from 'react'
import type { CSSProperties, RefObject } from 'react'
import { createPortal } from 'react-dom'
import { usePortalRoot } from './portal-root'
import styles from './select.module.css'

export type SelectOption<V extends string = string> = { value: V; label: string; short?: string; disabled?: boolean }

type Props<V extends string> = {
  ref: RefObject<HTMLDivElement | null>
  anchor: RefObject<HTMLDivElement | null>
  small: boolean
  id: string
  options: readonly SelectOption<V>[]
  value: V | null
  active: number
  columns: number
  round: boolean
  sheet: boolean
  title: string
  hint?: string
  onActive: (index: number) => void
  onPick: (index: number) => void
  onClose: () => void
}

const GAP = 8

const Spark = () => (
  <svg className={styles.spark} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2l2.2 7.8L22 12l-7.8 2.2L12 22l-2.2-7.8L2 12l7.8-2.2z" />
  </svg>
)

const cx = (...names: (string | false | undefined)[]) => names.filter(Boolean).join(' ')

export function SelectPopup<V extends string>({ ref, anchor, small, id, options, value, active, columns, round, sheet, title, hint, onActive, onPick, onClose }: Props<V>) {
  const items = useRef<(HTMLDivElement | null)[]>([])
  const pill = useRef<HTMLDivElement>(null)
  const host = useRef<HTMLDivElement>(null)
  const grid = columns > 1 || sheet
  const root = usePortalRoot()

  useLayoutEffect(() => {
    if (sheet) return
    let frame = 0
    const place = () => {
      const box = anchor.current?.getBoundingClientRect()
      const el = host.current
      if (!box || !el) return
      el.style.top = `${box.top}px`
      el.style.left = `${box.left}px`
      el.style.width = `${box.width}px`
      el.style.height = `${box.height}px`
    }
    const schedule = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(place)
    }
    place()
    window.addEventListener('scroll', schedule, true)
    window.addEventListener('resize', schedule)
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('scroll', schedule, true)
      window.removeEventListener('resize', schedule)
    }
  }, [anchor, sheet])

  useLayoutEffect(() => {
    const el = ref.current
    if (!el || sheet) return
    const rect = el.getBoundingClientRect()
    const anchor = el.parentElement?.getBoundingClientRect()
    if (anchor && rect.bottom > window.innerHeight - GAP && anchor.top - GAP > rect.height) el.classList.add(styles.up ?? '')
  }, [ref, sheet])

  useLayoutEffect(() => {
    const item = items.current[active]
    if (!item) return
    item.scrollIntoView({ block: 'nearest' })
    if (pill.current) {
      pill.current.style.transform = `translateY(${item.offsetTop}px)`
      pill.current.style.opacity = '1'
    }
  }, [active])

  const cell = (option: SelectOption<V>, i: number) => (
    <div
      key={option.value}
      ref={(el) => {
        items.current[i] = el
      }}
      id={`${id}-${i}`}
      role="option"
      aria-selected={option.value === value}
      aria-disabled={option.disabled || undefined}
      className={cx(
        grid ? styles.cell : styles.option,
        grid && round && styles.round,
        option.value === value && styles.selected,
        i === active && styles.active,
        option.disabled && styles.off,
      )}
      onPointerEnter={() => onActive(i)}
      onClick={() => onPick(i)}
    >
      {grid ? (sheet && columns <= 4 ? option.label : (option.short ?? option.label)) : <span>{option.label}</span>}
      {!grid && <Spark />}
    </div>
  )

  const gridVars: CSSProperties & Record<'--columns', number> = { '--columns': columns }

  const body = grid ? (
    <div id={id} role="listbox" className={styles.grid} style={gridVars}>
      {options.map(cell)}
    </div>
  ) : (
    <div id={id} role="listbox" className={styles.list}>
      <div ref={pill} className={styles.pill} />
      {options.map(cell)}
    </div>
  )

  if (sheet) {
    return createPortal(
      <div className={styles.sheetHost}>
        <div className={styles.scrim} onClick={onClose} />
        <div ref={ref} className={styles.sheet}>
          <div className={styles.grab} />
          <p className={styles.sheetTitle}>{title}</p>
          {hint && <p className={styles.sheetHint}>{hint}</p>}
          {body}
        </div>
      </div>,
      root,
    )
  }

  return createPortal(
    <div ref={host} className={cx(styles.floating, small && styles.sm)}>
      <div ref={ref} className={cx(styles.popup, grid && styles.gridPopup, round && styles.roundPopup)}>
        {grid && (
          <div className={styles.head}>
            <span className={styles.headTitle}>{title}</span>
            {hint && <span>{hint}</span>}
          </div>
        )}
        {body}
      </div>
    </div>,
    root,
  )
}
